/**
 * PSP/CRE Tool v2.0 - Lines of Communication (LOC) Configuration Constants
 * 
 * Purpose: Define road conditions, LOC security, bridges, and travel time modifiers
 * for medical evacuation planning with Ukraine 2022-2024 lessons learned.
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: December 21, 2024
 * Version: 2.0 - Enhanced with Ukraine combat data and Romania-specific routes
 * 
 * References:
 * - NATO STANAG 2154 (Road Movement Regulations)
 * - NATO STANAG 2021 (Military Load Classification)
 * - AJP-4.4 Allied Joint Movement and Transportation Doctrine
 * - FM 4-01.45 Multi-Service Tactics for Convoy Security
 * - Ukraine 2022-2024 Ground Evacuation After-Action Reports
 * - Romanian Road Infrastructure Standards (CNADNR)
 * 
 * Key Ukraine Lessons (2022-2024):
 * - Drone surveillance makes convoy movement high-risk
 * - Artillery interdiction creates route "no-go" windows
 * - Rasputitsa (mud season) renders unpaved routes impassable
 * - Bridge destruction is primary interdiction tactic
 * - Night movement safer but slower
 * - FPV drones target medical vehicles specifically
 * 
 * Usage:
 * import { 
 *   ROAD_STATUS, 
 *   LOC_SECURITY, 
 *   BRIDGE_STATUS,
 *   UKRAINE_ROUTE_LESSONS,
 *   calculateTravelTime,
 *   calculateEvacuationWindow 
 * } from '../constants/locConfig';
 */

// ============================================================================
// ROAD STATUS
// Condition of road surface affecting travel speed
// Enhanced with Ukraine 2022-2024 combat damage patterns
// ============================================================================

export const ROAD_STATUS = {
  PAVED: {
    value: 'PAVED',
    label: 'Paved Road',
    shortLabel: 'PAVED',
    description: 'Asphalt or concrete surface in good condition',
    icon: '🛣️',
    color: 'success',
    
    // Speed modifiers (km/h) - Updated with Ukraine observations
    speeds: {
      ambulance: 70,           // Max ambulance speed
      ambulance_armored: 55,   // MRAP/armored ambulance
      convoy: 40,              // Convoy formation speed
      single_vehicle: 80,      // Individual vehicle
      tracked: 35,             // Tracked vehicles
      litter_sensitive: 50     // When carrying unstable T1 patients
    },
    
    // Impact factors
    speed_modifier: 1.0,
    vehicle_wear: 1.0,
    fuel_consumption: 1.0,
    patient_comfort: 1.0,       // Vibration impact on patients
    t1_transport_safe: true,    // Safe for critical patients
    
    // Movement capacity
    vehicles_per_hour: 200,     // Throughput per lane
    night_speed_modifier: 0.8,
    
    // Ukraine-based damage probability
    artillery_damage_susceptibility: 0.3,  // Relatively resilient
    
    notes: 'Optimal conditions for medical evacuation.',
    educational_note: 'Paved roads allow fastest evacuation but are easily observed by enemy drones in Ukraine conflict.'
  },
  
  IMPROVED: {
    value: 'IMPROVED',
    label: 'Improved Surface',
    shortLabel: 'IMPROVED',
    description: 'Gravel or stabilized surface, well-maintained',
    icon: '🛤️',
    color: 'info',
    
    speeds: {
      ambulance: 50,
      ambulance_armored: 40,
      convoy: 30,
      single_vehicle: 60,
      tracked: 30,
      litter_sensitive: 35
    },
    
    speed_modifier: 0.75,
    vehicle_wear: 1.3,
    fuel_consumption: 1.2,
    patient_comfort: 0.85,
    t1_transport_safe: true,
    
    vehicles_per_hour: 150,
    night_speed_modifier: 0.6,
    
    artillery_damage_susceptibility: 0.5,
    
    notes: 'Acceptable for evacuation with some delay. Consider patient condition.',
    educational_note: 'Gravel roads less visible but produce dust signatures in dry conditions.'
  },
  
  UNPAVED: {
    value: 'UNPAVED',
    label: 'Unpaved/Dirt Road',
    shortLabel: 'UNPAVED',
    description: 'Dirt or natural surface, weather-dependent',
    icon: '🏔️',
    color: 'warning',
    
    speeds: {
      ambulance: 35,
      ambulance_armored: 25,
      convoy: 20,
      single_vehicle: 45,
      tracked: 25,
      litter_sensitive: 20
    },
    
    speed_modifier: 0.5,
    vehicle_wear: 2.0,
    fuel_consumption: 1.5,
    patient_comfort: 0.6,
    t1_transport_safe: false,  // Vibration risk for critical patients
    
    vehicles_per_hour: 80,
    night_speed_modifier: 0.4,
    
    // Weather impacts - Critical for Ukraine/Romania
    weather_impacts: {
      CLEAR: 1.0,
      RAIN: 0.5,
      HEAVY_RAIN: 0.3,
      SNOW: 0.3,
      RASPUTITSA: 0.1,        // Spring/autumn mud season
      MUD: 0.2,
      FROZEN: 0.7             // Frozen ground actually improves
    },
    
    artillery_damage_susceptibility: 0.7,
    
    notes: 'Significant delay. May be impassable in poor weather. T1 patients may deteriorate.',
    educational_note: 'Rasputitsa (mud season) in Ukraine/Romania renders unpaved routes nearly impassable for 4-6 weeks in spring and autumn.'
  },
  
  DAMAGED: {
    value: 'DAMAGED',
    label: 'Damaged/Cratered',
    shortLabel: 'DAMAGED',
    description: 'Battle-damaged surface with craters and debris',
    icon: '💥',
    color: 'error',
    
    speeds: {
      ambulance: 15,
      ambulance_armored: 12,
      convoy: 10,
      single_vehicle: 20,
      tracked: 20,
      litter_sensitive: 8       // Very dangerous for critical patients
    },
    
    speed_modifier: 0.25,
    vehicle_wear: 4.0,
    fuel_consumption: 2.0,
    patient_comfort: 0.3,
    t1_transport_safe: false,
    
    vehicles_per_hour: 30,
    night_speed_modifier: 0.2,
    
    requires_engineering: true,
    bypass_recommended: true,
    
    // Ukraine crater density patterns
    crater_types: {
      ARTILLERY_152MM: { diameter_m: 3, depth_m: 1.5, spacing_avg_m: 50 },
      ARTILLERY_122MM: { diameter_m: 2, depth_m: 1.0, spacing_avg_m: 40 },
      MORTAR_120MM: { diameter_m: 1.5, depth_m: 0.8, spacing_avg_m: 30 },
      GLIDE_BOMB: { diameter_m: 15, depth_m: 5, impassable: true }
    },
    
    artillery_damage_susceptibility: 0.9,
    
    notes: 'Major evacuation delays. Engineering support required. Consider alternate routes or air evacuation.',
    educational_note: 'Ukraine data: Artillery-damaged roads require 15-30 minutes engineering clearance per 100m.'
  },
  
  DENIED: {
    value: 'DENIED',
    label: 'Denied/Impassable',
    shortLabel: 'DENIED',
    description: 'Route blocked, destroyed, or under enemy control',
    icon: '⛔',
    color: 'error',
    
    speeds: {
      ambulance: 0,
      ambulance_armored: 0,
      convoy: 0,
      single_vehicle: 0,
      tracked: 0,
      litter_sensitive: 0
    },
    
    speed_modifier: 0,
    vehicle_wear: 'N/A',
    fuel_consumption: 'N/A',
    patient_comfort: 'N/A',
    t1_transport_safe: false,
    
    vehicles_per_hour: 0,
    night_speed_modifier: 0,
    
    requires_engineering: true,
    bypass_required: true,
    
    notes: 'Route unusable. Alternate routing mandatory. Air evacuation may be only option.',
    educational_note: 'Denied routes in Ukraine typically require 24-72 hours to restore with engineering assets.'
  }
};

// ============================================================================
// LOC SECURITY LEVELS
// Security situation along routes - Enhanced with Ukraine patterns
// ============================================================================

export const LOC_SECURITY = {
  SECURE: {
    value: 'SECURE',
    label: 'Secure',
    shortLabel: 'SECURE',
    description: 'Route under friendly control, minimal threat',
    icon: '✅',
    color: 'success',
    
    movement_window: '24_HOURS',
    escort_required: false,
    armored_vehicles_required: false,
    convoy_minimum_size: 1,
    medical_markings_safe: true,    // Can display red cross
    
    speed_modifier: 1.0,
    delay_minutes: 0,
    
    // Risk factors
    ambush_probability: 0.01,
    ied_probability: 0.01,
    sniper_probability: 0.01,
    fpv_drone_probability: 0.02,     // Ukraine: Even rear areas have drone risk
    artillery_probability: 0.01,
    
    radio_check_interval: 60,
    
    evacuation_paradigm: 'PERMISSIVE',
    
    notes: 'Normal movement. Standard procedures apply.',
    educational_note: 'Even "secure" routes in Ukraine have 2% FPV drone intercept risk - maintain dispersion.'
  },
  
  GUARDED: {
    value: 'GUARDED',
    label: 'Guarded',
    shortLabel: 'GUARDED',
    description: 'Occasional incidents, enhanced awareness required',
    icon: '⚠️',
    color: 'info',
    
    movement_window: '24_HOURS',
    escort_required: false,
    armored_vehicles_required: false,
    convoy_minimum_size: 2,
    medical_markings_safe: true,
    
    speed_modifier: 0.9,
    delay_minutes: 10,
    
    ambush_probability: 0.05,
    ied_probability: 0.05,
    sniper_probability: 0.03,
    fpv_drone_probability: 0.08,
    artillery_probability: 0.03,
    
    radio_check_interval: 30,
    
    evacuation_paradigm: 'PERMISSIVE',
    
    notes: 'Heightened awareness. Avoid stopping. Report suspicious activity.',
    educational_note: 'Guarded routes may have ISR drone coverage - expect to be observed.'
  },
  
  CONTESTED: {
    value: 'CONTESTED',
    label: 'Contested',
    shortLabel: 'CONTESTED',
    description: 'Active enemy activity, escort recommended',
    icon: '🔫',
    color: 'warning',
    
    movement_window: 'DAYLIGHT_PREFERRED',
    escort_required: true,
    armored_vehicles_required: true,
    convoy_minimum_size: 4,
    medical_markings_safe: false,   // Ukraine: Medical vehicles targeted
    
    speed_modifier: 0.7,
    delay_minutes: 30,
    
    ambush_probability: 0.15,
    ied_probability: 0.20,
    sniper_probability: 0.10,
    fpv_drone_probability: 0.25,    // Significant drone threat
    artillery_probability: 0.15,
    
    radio_check_interval: 15,
    
    escort_size: {
      small_convoy: 2,
      medium_convoy: 4,
      large_convoy: 6
    },
    
    evacuation_paradigm: 'CONTESTED',
    
    // Ukraine movement windows
    safe_movement_windows: {
      dawn: { start: '05:00', end: '07:00', risk_mod: 0.7 },
      dusk: { start: '18:00', end: '20:00', risk_mod: 0.7 },
      night: { start: '22:00', end: '04:00', risk_mod: 0.5 }
    },
    
    notes: 'Armed escort required. Coordinate with Route Security. Armored ambulances recommended.',
    educational_note: 'Ukraine data: Medical vehicles 3x more likely to be targeted in contested areas - use camouflage.'
  },
  
  INTERDICTED: {
    value: 'INTERDICTED',
    label: 'Interdicted',
    shortLabel: 'INTERDICTED',
    description: 'Heavy enemy activity, route regularly attacked',
    icon: '🚨',
    color: 'error',
    
    movement_window: 'COORDINATED_ONLY',
    escort_required: true,
    armored_vehicles_required: true,
    convoy_minimum_size: 8,
    medical_markings_safe: false,
    route_clearance_required: true,
    
    speed_modifier: 0.4,
    delay_minutes: 60,
    
    ambush_probability: 0.35,
    ied_probability: 0.40,
    sniper_probability: 0.20,
    fpv_drone_probability: 0.50,
    artillery_probability: 0.40,
    
    radio_check_interval: 5,
    
    // QRF requirements
    qrf_standby: true,
    qrf_response_minutes: 15,
    
    evacuation_paradigm: 'CONTESTED',
    
    // Movement only during artillery lulls
    artillery_window_required: true,
    artillery_lull_minimum_minutes: 30,
    
    notes: 'High-risk route. QRF on standby. Heavy escort required. Route clearance before movement.',
    educational_note: 'Interdicted routes in Ukraine have 50% FPV drone engagement probability - consider air evacuation.'
  },
  
  DENIED: {
    value: 'DENIED',
    label: 'Denied',
    shortLabel: 'DENIED',
    description: 'Route under enemy control or observation',
    icon: '🛑',
    color: 'error',
    
    movement_window: 'NONE',
    escort_required: true,
    armored_vehicles_required: true,
    convoy_minimum_size: 'N/A',
    medical_markings_safe: false,
    
    speed_modifier: 0,
    delay_minutes: 'N/A',
    
    ambush_probability: 1.0,
    ied_probability: 0.80,
    sniper_probability: 0.50,
    fpv_drone_probability: 0.90,
    artillery_probability: 0.80,
    
    radio_check_interval: 'N/A',
    
    evacuation_paradigm: 'DENIED',
    
    notes: 'Movement denied. Find alternate route. Air evacuation only option if available.',
    educational_note: 'Denied routes in Ukraine require offensive action to restore - plan for 72+ hour closure.'
  }
};

// ============================================================================
// DRONE ACTIVITY LEVELS
// Ukraine 2022-2024: FPV drones are primary threat to ground evacuation
// ============================================================================

export const DRONE_ACTIVITY = {
  NONE: {
    value: 'NONE',
    label: 'No Drone Activity',
    shortLabel: 'NONE',
    description: 'No reported enemy drone operations in area',
    icon: '✅',
    color: 'success',
    
    // Modifiers
    speed_modifier: 1.0,           // No speed reduction needed
    risk_modifier: 1.0,            // Baseline risk
    night_effectiveness: 0.0,      // No drones to worry about
    day_risk_multiplier: 1.0,
    
    recommendations: [
      'Standard movement procedures',
      'Maintain observation protocols'
    ],
    
    educational_note: 'Rare in modern conflict - always assume some drone presence.'
  },
  
  LOW: {
    value: 'LOW',
    label: 'Low Drone Activity',
    shortLabel: 'LOW',
    description: 'Occasional ISR drones, minimal FPV threat',
    icon: '🔵',
    color: 'info',
    
    speed_modifier: 0.95,
    risk_modifier: 1.1,
    night_effectiveness: 0.2,      // Thermal drones have some capability
    day_risk_multiplier: 1.2,
    
    recommendations: [
      'Maintain dispersion between vehicles',
      'Use covered routes when available'
    ],
    
    educational_note: 'Ukraine "quiet sectors" still had ISR coverage - expect observation.'
  },
  
  NORMAL: {
    value: 'NORMAL',
    label: 'Normal Drone Activity',
    shortLabel: 'NORMAL',
    description: 'Regular ISR and occasional FPV drone presence',
    icon: '🟡',
    color: 'warning',
    
    speed_modifier: 0.9,
    risk_modifier: 1.3,
    night_effectiveness: 0.4,      // FPV drones less effective at night
    day_risk_multiplier: 1.5,
    
    recommendations: [
      'Consider night movement for sensitive evacuations',
      'Use camouflage on medical vehicles',
      'Maintain 100m+ vehicle spacing'
    ],
    
    educational_note: 'Standard Ukraine front-line condition - assume being observed.'
  },
  
  HIGH: {
    value: 'HIGH',
    label: 'High Drone Activity',
    shortLabel: 'HIGH',
    description: 'Active FPV drone attacks reported in area',
    icon: '🟠',
    color: 'warning',
    
    speed_modifier: 0.8,
    risk_modifier: 1.8,
    night_effectiveness: 0.5,
    day_risk_multiplier: 2.5,
    
    recommendations: [
      'Night movement strongly recommended',
      'Armored ambulances required',
      'Consider air evacuation for T1 patients',
      'Counter-UAS coverage if available'
    ],
    
    educational_note: 'Ukraine 2023-2024: High drone areas had 25%+ vehicle loss rates.'
  },
  
  EXTREME: {
    value: 'EXTREME',
    label: 'Extreme Drone Threat',
    shortLabel: 'EXTREME',
    description: 'Continuous drone coverage, active hunting operations',
    icon: '🔴',
    color: 'error',
    
    speed_modifier: 0.6,
    risk_modifier: 3.0,
    night_effectiveness: 0.6,      // Even night movement risky
    day_risk_multiplier: 5.0,
    
    recommendations: [
      'Ground evacuation NOT recommended',
      'Air evacuation or prolonged field care',
      'If ground movement essential: night only, armored, with EW support',
      'Accept 30%+ casualty risk to convoy'
    ],
    
    educational_note: 'Ukraine Bakhmut/Avdiivka pattern - ground movement effectively suicide mission.'
  }
};

// ============================================================================
// BRIDGE STATUS
// Critical infrastructure - Primary interdiction target in Ukraine
// ============================================================================

export const BRIDGE_STATUS = {
  INTACT: {
    value: 'INTACT',
    label: 'Intact',
    shortLabel: 'INTACT',
    description: 'Bridge fully operational, no damage',
    icon: '🌉',
    color: 'success',
    
    usable: true,
    mlc_rating: 70,            // Military Load Classification
    vehicles_per_hour: 60,
    delay_per_vehicle_seconds: 10,
    
    inspection_required: false,
    
    notes: 'Full capacity crossing.',
    educational_note: 'Intact bridges are high-value targets - expect interdiction attempts.'
  },
  
  RESTRICTED: {
    value: 'RESTRICTED',
    label: 'Restricted Load',
    shortLabel: 'RESTRICTED',
    description: 'Bridge has load/speed restrictions',
    icon: '⚖️',
    color: 'info',
    
    usable: true,
    mlc_rating: 40,
    mlc_wheeled: 50,
    mlc_tracked: 30,
    vehicles_per_hour: 30,
    delay_per_vehicle_seconds: 30,
    speed_limit_kmh: 10,
    single_lane: true,
    
    inspection_required: true,
    inspection_interval_hours: 24,
    
    // Vehicle restrictions
    restrictions: {
      max_vehicle_weight_tons: 20,
      max_axle_weight_tons: 8,
      single_vehicle_crossing: true,
      tracked_prohibited: false
    },
    
    notes: 'Single vehicle crossing. Weight restrictions apply.',
    educational_note: 'Restricted bridges can create evacuation bottlenecks - pre-plan alternates.'
  },
  
  TEMPORARY: {
    value: 'TEMPORARY',
    label: 'Temporary/Bailey',
    shortLabel: 'TEMP',
    description: 'Engineer-emplaced temporary crossing',
    icon: '🔧',
    color: 'warning',
    
    usable: true,
    mlc_rating: 60,           // Bailey bridge MLC 60 typical
    vehicles_per_hour: 20,
    delay_per_vehicle_seconds: 60,
    speed_limit_kmh: 8,
    single_lane: true,
    
    inspection_required: true,
    inspection_interval_hours: 8,
    
    // Temporary bridge types (Romania/NATO)
    bridge_types: {
      BAILEY: { mlc: 60, length_m: 30, setup_hours: 8 },
      MEDIUM_GIRDER: { mlc: 70, length_m: 50, setup_hours: 12 },
      RIBBON: { mlc: 50, length_m: 100, setup_hours: 4 },
      M3_AMPHIBIOUS: { mlc: 60, length_m: 100, setup_hours: 2 }
    },
    
    restrictions: {
      max_vehicle_weight_tons: 30,
      single_vehicle_crossing: true,
      tracked_caution: true
    },
    
    notes: 'Temporary crossing. Maintain weight limits. Engineer support on site.',
    educational_note: 'Romania maintains 3 engineer battalions with bridge assets - coordinate early.'
  },
  
  DAMAGED: {
    value: 'DAMAGED',
    label: 'Damaged',
    shortLabel: 'DAMAGED',
    description: 'Bridge partially damaged, limited use',
    icon: '⚠️',
    color: 'warning',
    
    usable: 'CONDITIONAL',
    mlc_rating: 20,
    vehicles_per_hour: 10,
    delay_per_vehicle_seconds: 120,
    speed_limit_kmh: 5,
    
    inspection_required: true,
    inspection_interval_hours: 4,
    engineer_on_site: true,
    
    restrictions: {
      max_vehicle_weight_tons: 10,
      wheeled_only: true,
      single_vehicle: true,
      ambulance_approved: true,  // Light vehicles OK
      tracked_prohibited: true
    },
    
    // Damage assessment categories
    damage_levels: {
      MINOR: { capacity_percent: 60, repair_hours: 8 },
      MODERATE: { capacity_percent: 30, repair_hours: 24 },
      SEVERE: { capacity_percent: 10, repair_hours: 72 }
    },
    
    notes: 'Engineer assessment required. Ambulances may cross with caution.',
    educational_note: 'Damaged bridges in Ukraine often collapse during use - ensure engineer clearance.'
  },
  
  DESTROYED: {
    value: 'DESTROYED',
    label: 'Destroyed',
    shortLabel: 'DESTROYED',
    description: 'Bridge destroyed or collapsed',
    icon: '❌',
    color: 'error',
    
    usable: false,
    mlc_rating: 0,
    vehicles_per_hour: 0,
    
    bypass_required: true,
    fording_check: true,       // Check if ford crossing possible
    
    // Alternative crossing options
    alternatives: {
      fording: { 
        requires: 'Shallow water, firm bottom',
        max_depth_m: 0.8,
        suitable_vehicles: ['tracked', 'high-clearance']
      },
      ferry: {
        requires: 'Engineer ferry assets',
        capacity_vehicles: 4,
        crossing_time_minutes: 20
      },
      alternate_route: {
        requires: 'Route reconnaissance',
        typical_detour_km: 15
      }
    },
    
    notes: 'Bridge impassable. Seek alternate crossing. Report to engineers.',
    educational_note: 'Ukraine bridges average 48 hours to replace with military bridging - plan for delays.'
  }
};

// ============================================================================
// ROUTE TYPES
// Classifications for military route planning
// ============================================================================

export const ROUTE_TYPES = {
  MSR: {
    value: 'MSR',
    label: 'Main Supply Route',
    shortLabel: 'MSR',
    description: 'Primary route for supplies and evacuation',
    priority: 1,
    
    characteristics: {
      width_m: 7.0,
      lanes: 2,
      hard_surface: true,
      bridge_mlc: 70,
      convoy_capable: true
    },
    
    traffic_priority: {
      medical: 1,
      ammunition: 2,
      fuel: 3,
      personnel: 4
    },
    
    notes: 'Primary evacuation route. Maintain at all costs.'
  },
  
  ASR: {
    value: 'ASR',
    label: 'Alternate Supply Route',
    shortLabel: 'ASR',
    description: 'Secondary route, backup to MSR',
    priority: 2,
    
    characteristics: {
      width_m: 5.5,
      lanes: 2,
      hard_surface: 'partial',
      bridge_mlc: 50,
      convoy_capable: true
    },
    
    notes: 'Use when MSR interdicted or congested.'
  },
  
  SUPPLY_ROUTE: {
    value: 'SUPPLY_ROUTE',
    label: 'Supply Route',
    shortLabel: 'SR',
    description: 'Designated logistics route',
    priority: 3,
    
    characteristics: {
      width_m: 4.5,
      lanes: 1,
      hard_surface: 'variable',
      bridge_mlc: 40,
      convoy_capable: 'limited'
    },
    
    notes: 'Tertiary option. May have restrictions.'
  },
  
  AXIS: {
    value: 'AXIS',
    label: 'Axis of Advance',
    shortLabel: 'AXIS',
    description: 'Combat movement route, not for logistics',
    priority: 4,
    
    characteristics: {
      width_m: 'variable',
      lanes: 'variable',
      hard_surface: 'variable',
      bridge_mlc: 'variable',
      convoy_capable: false
    },
    
    notes: 'Combat movement only. Not suitable for routine evacuation.',
    educational_note: 'Axis routes often have active combat - avoid for evacuation unless emergency.'
  }
};

// ============================================================================
// WEATHER ROUTE IMPACTS
// Weather effects on ground movement - Romania/Ukraine specific
// ============================================================================

export const WEATHER_ROUTE_IMPACTS = {
  CLEAR: {
    value: 'CLEAR',
    label: 'Clear',
    description: 'Good visibility and road conditions',
    speed_modifier: 1.0,
    visibility_km: 10,
    
    road_impact: {
      PAVED: 1.0,
      IMPROVED: 1.0,
      UNPAVED: 1.0,
      DAMAGED: 1.0
    },
    
    night_modifier: 0.8,
    convoy_spacing_increase: 1.0,
    
    drone_threat_modifier: 1.0,  // Full drone capability
    
    notes: 'Optimal conditions. Normal operations.'
  },
  
  RAIN: {
    value: 'RAIN',
    label: 'Rain',
    description: 'Moderate rainfall affecting visibility and roads',
    speed_modifier: 0.7,
    visibility_km: 3,
    
    road_impact: {
      PAVED: 0.85,
      IMPROVED: 0.70,
      UNPAVED: 0.50,
      DAMAGED: 0.40
    },
    
    night_modifier: 0.5,
    convoy_spacing_increase: 1.5,
    
    drone_threat_modifier: 0.6,   // Reduced drone operations
    
    notes: 'Reduced speeds on unpaved routes. Increased braking distance.',
    educational_note: 'Rain grounds most FPV drones - may provide safer movement windows.'
  },
  
  HEAVY_RAIN: {
    value: 'HEAVY_RAIN',
    label: 'Heavy Rain',
    description: 'Heavy rainfall, possible flooding',
    speed_modifier: 0.5,
    visibility_km: 1,
    
    road_impact: {
      PAVED: 0.70,
      IMPROVED: 0.50,
      UNPAVED: 0.30,
      DAMAGED: 0.20
    },
    
    night_modifier: 0.3,
    convoy_spacing_increase: 2.0,
    
    flooding_risk: true,
    ford_crossing_prohibited: true,
    
    drone_threat_modifier: 0.2,
    
    notes: 'Significant delays. Check for flooding. Unpaved routes may be impassable.'
  },
  
  SNOW: {
    value: 'SNOW',
    label: 'Snow',
    description: 'Snowfall affecting visibility and traction',
    speed_modifier: 0.5,
    visibility_km: 2,
    
    road_impact: {
      PAVED: 0.60,
      IMPROVED: 0.50,
      UNPAVED: 0.30,
      DAMAGED: 0.25
    },
    
    night_modifier: 0.4,
    convoy_spacing_increase: 2.5,
    
    chains_required: true,
    patient_hypothermia_risk: true,
    
    drone_threat_modifier: 0.4,
    
    // Romania Carpathian considerations
    mountain_closure_likely: true,
    altitude_threshold_m: 1000,
    
    notes: 'Snow chains required. Monitor patient temperature. Mountain passes may close.',
    educational_note: 'Carpathian passes close rapidly in snow - have alternate lowland routes planned.'
  },
  
  RASPUTITSA: {
    value: 'RASPUTITSA',
    label: 'Rasputitsa (Mud Season)',
    description: 'Spring/autumn thaw creating impassable mud',
    speed_modifier: 0.3,
    visibility_km: 5,
    
    road_impact: {
      PAVED: 0.90,           // Paved OK
      IMPROVED: 0.40,        // Gravel degrades
      UNPAVED: 0.10,         // Nearly impassable
      DAMAGED: 0.10
    },
    
    night_modifier: 0.5,
    convoy_spacing_increase: 3.0,
    
    tracked_required_unpaved: true,
    wheeled_stuck_risk: 0.60,   // 60% chance of getting stuck
    
    // Ukraine seasonal patterns
    typical_duration_weeks: 6,
    spring_dates: { start: 'March 15', end: 'April 30' },
    autumn_dates: { start: 'October 15', end: 'November 30' },
    
    drone_threat_modifier: 0.7,
    
    notes: 'Unpaved routes effectively denied. Plan for paved-only movement.',
    educational_note: 'Rasputitsa in Ukraine 2022-2024 immobilized Russian advances - respect mud season.'
  },
  
  FOG: {
    value: 'FOG',
    label: 'Fog',
    description: 'Reduced visibility from fog',
    speed_modifier: 0.4,
    visibility_km: 0.2,
    
    road_impact: {
      PAVED: 0.60,
      IMPROVED: 0.50,
      UNPAVED: 0.40,
      DAMAGED: 0.30
    },
    
    night_modifier: 0.2,
    convoy_spacing_increase: 3.0,
    
    helicopter_grounded: true,
    navigation_impaired: true,
    
    drone_threat_modifier: 0.1,   // Drones ineffective
    
    notes: 'Air evacuation grounded. Ground movement slow but safer from drones.',
    educational_note: 'Fog provides cover from observation but increases ground accident risk.'
  },
  
  DUST_STORM: {
    value: 'DUST_STORM',
    label: 'Dust Storm',
    description: 'High winds with dust/sand reducing visibility',
    speed_modifier: 0.3,
    visibility_km: 0.1,
    
    road_impact: {
      PAVED: 0.50,
      IMPROVED: 0.40,
      UNPAVED: 0.30,
      DAMAGED: 0.20
    },
    
    night_modifier: 0.2,
    convoy_spacing_increase: 3.0,
    
    helicopter_grounded: true,
    vehicle_damage_risk: true,
    patient_respiratory_risk: true,
    
    drone_threat_modifier: 0.05,
    
    notes: 'Severe conditions. Shelter in place if possible. Air evacuation denied.',
    educational_note: 'Dust storms rare in Romania but common in deployment areas - plan accordingly.'
  },
  
  EXTREME_COLD: {
    value: 'EXTREME_COLD',
    label: 'Extreme Cold (<-20°C)',
    description: 'Severe cold affecting vehicles and personnel',
    speed_modifier: 0.6,
    visibility_km: 10,
    
    road_impact: {
      PAVED: 0.80,           // Ice risk
      IMPROVED: 0.70,
      UNPAVED: 0.60,
      DAMAGED: 0.50
    },
    
    night_modifier: 0.5,
    convoy_spacing_increase: 1.5,
    
    vehicle_warmup_minutes: 30,
    patient_hypothermia_critical: true,
    battery_failure_risk: 0.20,
    fuel_gelling_risk: true,
    
    drone_threat_modifier: 0.5,   // Batteries affected
    
    notes: 'Pre-warm vehicles. Protect patients from cold. Monitor fuel.',
    educational_note: 'Ukraine winters reach -30°C - cold casualties can exceed combat casualties.'
  }
};

// ============================================================================
// UKRAINE ROUTE LESSONS (2022-2024)
// Empirical data from active conflict zone
// ============================================================================

export const UKRAINE_ROUTE_LESSONS = {
  // Observed patterns
  MEDICAL_VEHICLE_TARGETING: {
    description: 'Medical vehicles specifically targeted despite markings',
    targeting_multiplier: 3.0,   // 3x more likely than combat vehicles
    
    recommendations: [
      'Remove or cover medical markings in contested areas',
      'Use armored ambulances exclusively in forward areas',
      'Maintain tactical dispersion',
      'Vary routes and timing'
    ],
    
    source: 'Ukraine Medical Corps AAR 2023'
  },
  
  FPV_DRONE_INTERDICTION: {
    description: 'FPV kamikaze drones primary threat to ground evacuation',
    
    engagement_patterns: {
      day_clear: 0.40,          // 40% engagement rate
      day_overcast: 0.30,
      night: 0.15,
      rain: 0.10,
      fog: 0.02
    },
    
    countermeasures: [
      'Electronic warfare support',
      'Night movement preferred',
      'Weather windows exploitation',
      'Dispersion and speed'
    ],
    
    source: 'NATO JALLC Ukraine Lessons 2024'
  },
  
  ARTILLERY_INTERDICTION_WINDOWS: {
    description: 'Artillery creates predictable no-go periods',
    
    typical_pattern: {
      bombardment_duration_minutes: 15,
      lull_duration_minutes: 45,
      cycle_predictability: 0.7
    },
    
    exploitation: 'Move during artillery lulls - coordinate with counter-battery',
    
    source: 'Ukraine Ground Forces Movement SOP'
  },
  
  BRIDGE_DESTRUCTION_PRIORITY: {
    description: 'Bridges primary interdiction target',
    
    statistics: {
      bridges_destroyed_2022_2024: 847,
      average_repair_days: 12,
      temporary_bridge_days: 2
    },
    
    recommendations: [
      'Pre-plan alternate crossings',
      'Coordinate with engineer units',
      'Maintain ferry/fording options',
      'Disperse crossing points'
    ],
    
    source: 'Ukraine Engineer Command Statistics'
  },
  
  NIGHT_MOVEMENT_EFFECTIVENESS: {
    description: 'Night movement significantly safer despite slower speed',
    
    casualty_reduction: 0.65,   // 65% fewer losses at night
    speed_reduction: 0.50,      // 50% slower
    net_benefit: 0.75,          // Still beneficial
    
    requirements: [
      'NVG availability',
      'Route familiarity',
      'Blackout discipline',
      'Pre-positioned guides'
    ],
    
    source: 'Ukraine Medical Evacuation Analysis 2023'
  },
  
  CONVOY_DISPERSION: {
    description: 'Dispersed movement more survivable than convoys',
    
    recommended_spacing: {
      secure: 50,               // meters
      guarded: 100,
      contested: 200,
      interdicted: 'individual_movement'
    },
    
    source: 'NATO Movement Security Handbook (Ukraine Update)'
  }
};

// ============================================================================
// ROMANIA-SPECIFIC ROUTE FACTORS
// Geographic and infrastructure considerations for Romanian theater
// ============================================================================

export const ROMANIA_ROUTE_FACTORS = {
  CARPATHIAN_PASSES: {
    description: 'Mountain passes with seasonal and weather restrictions',
    
    passes: {
      PREDEAL: { altitude_m: 1040, winter_closure_risk: 0.3 },
      BRAN: { altitude_m: 1290, winter_closure_risk: 0.4 },
      TRANSFAGARASAN: { altitude_m: 2042, winter_closure_risk: 0.9 },
      TRANSALPINA: { altitude_m: 2145, winter_closure_risk: 0.95 }
    },
    
    seasonal_restrictions: {
      november_march: 'HIGH_PASSES_CLOSED',
      april_may: 'UNPREDICTABLE',
      june_october: 'GENERALLY_OPEN'
    },
    
    medical_considerations: {
      altitude_sickness_risk: true,
      hypothermia_risk: true,
      evacuation_alternatives: 'Lowland routes or helicopter'
    }
  },
  
  DANUBE_CROSSINGS: {
    description: 'Critical Danube River crossing points',
    
    bridges: {
      GIURGIU_RUSE: { mlc: 80, status: 'intact' },
      CALAFAT_VIDIN: { mlc: 70, status: 'intact' },
      BRAILA: { mlc: 80, status: 'intact' },
      CERNAVODA: { mlc: 70, status: 'intact' }
    },
    
    ferry_options: {
      BECHET_ORYAHOVO: { capacity: 20, crossing_minutes: 30 },
      TURNU_MAGURELE: { capacity: 15, crossing_minutes: 25 }
    },
    
    strategic_note: 'Danube bridges high-value targets - plan redundancy'
  },
  
  EASTERN_BORDER_ROUTES: {
    description: 'Routes near Moldova/Ukraine border',
    
    characteristics: {
      quality: 'VARIABLE',
      width: 'NARROW',
      military_upgrade_status: 'IN_PROGRESS'
    },
    
    considerations: [
      'Limited alternate routes',
      'Single-lane bridges common',
      'Seasonal flooding in Prut valley'
    ]
  }
};

// ============================================================================
// HELPER FUNCTIONS
// Calculator integration with comprehensive travel time modeling
// ============================================================================

/**
 * Calculate travel time between two points with all factors
 * @param {number} distance_km - Distance in kilometers
 * @param {Object} locConfig - LOC configuration object
 * @returns {Object} Travel time details with survival impact
 */
export const calculateTravelTime = (distance_km, locConfig = {}) => {
  const {
    road_status = 'PAVED',
    loc_security = 'SECURE',
    bridge_status = 'INTACT',
    weather = 'CLEAR',
    vehicle_type = 'ambulance',
    is_convoy = false,
    convoy_size = 1,
    is_night = false,
    is_armored = false,
    patient_category = 'T2'    // T1, T2, T3, or DNBI
  } = locConfig;
  
  // Get base configuration data
  const road = ROAD_STATUS[road_status] || ROAD_STATUS.PAVED;
  const security = LOC_SECURITY[loc_security] || LOC_SECURITY.SECURE;
  const bridge = BRIDGE_STATUS[bridge_status] || BRIDGE_STATUS.INTACT;
  const weatherData = WEATHER_ROUTE_IMPACTS[weather] || WEATHER_ROUTE_IMPACTS.CLEAR;
  
  // Determine vehicle speed type
  let vehicleSpeedKey = is_convoy ? 'convoy' : vehicle_type;
  if (is_armored && road.speeds.ambulance_armored) {
    vehicleSpeedKey = 'ambulance_armored';
  }
  if (patient_category === 'T1' && road.speeds.litter_sensitive) {
    vehicleSpeedKey = 'litter_sensitive';  // Slower for critical patients
  }
  
  // Get base speed
  let baseSpeed = road.speeds[vehicleSpeedKey] || road.speeds.ambulance || 50;
  
  // Apply modifiers
  let effectiveSpeed = baseSpeed;
  effectiveSpeed *= security.speed_modifier || 1.0;
  effectiveSpeed *= weatherData.road_impact[road_status] || 1.0;
  
  // Night operations modifier
  if (is_night) {
    effectiveSpeed *= road.night_speed_modifier || 0.8;
    effectiveSpeed *= weatherData.night_modifier || 0.8;
  }
  
  // Minimum speed floor
  effectiveSpeed = Math.max(effectiveSpeed, 5);
  
  // Calculate base travel time (hours)
  let travelTime_hours = distance_km / effectiveSpeed;
  
  // Calculate delays
  let delayMinutes = 0;
  
  // Security-related delays
  // 2026-10-19 (R2-01): a security level with no movement window (DENIED) has the text 'N/A' as delay; a text added to a number made the
  // time to surgery NaN and stopped the calculation. The loss of movement is in the speed (speed_modifier 0, floor in threatEffects).
  delayMinutes += Number.isFinite(security.delay_minutes) ? security.delay_minutes : 0;
  
  // Bridge delays
  if (bridge.delay_per_vehicle_seconds) {
    const vehicles = is_convoy ? convoy_size : 1;
    delayMinutes += (bridge.delay_per_vehicle_seconds * vehicles) / 60;
  }
  
  // Escort coordination time
  if (security.escort_required) {
    delayMinutes += 15;  // Escort linkup
  }
  
  // Route clearance time
  if (security.route_clearance_required) {
    delayMinutes += 45;  // Route clearance operation
  }
  
  // Convert delays to hours
  travelTime_hours += delayMinutes / 60;
  
  // Round to minutes
  const totalMinutes = Math.round(travelTime_hours * 60);
  
  // Calculate survival impact for T1 patients
  let survivalImpact = null;
  if (patient_category === 'T1') {
    // Golden hour calculation
    const goldenHourExceeded = totalMinutes > 60;
    const platinumPeriodExceeded = totalMinutes > 10;
    
    // Survival probability based on time (simplified curve)
    let survivalProbability;
    if (totalMinutes <= 60) {
      survivalProbability = 0.92 - (totalMinutes / 60) * 0.16;
    } else if (totalMinutes <= 120) {
      survivalProbability = 0.76 - ((totalMinutes - 60) / 60) * 0.22;
    } else if (totalMinutes <= 180) {
      survivalProbability = 0.54 - ((totalMinutes - 120) / 60) * 0.22;
    } else {
      survivalProbability = 0.32 - Math.min(0.14, (totalMinutes - 180) / 180 * 0.14);
    }
    
    survivalImpact = {
      travel_time_minutes: totalMinutes,
      golden_hour_exceeded: goldenHourExceeded,
      platinum_period_exceeded: platinumPeriodExceeded,
      survival_probability: Math.round(survivalProbability * 100),
      recommendation: goldenHourExceeded ? 
        'Consider air evacuation or Prolonged Field Care' : 
        'Ground evacuation acceptable'
    };
  }
  
  // Calculate risk factors
  const riskAssessment = {
    ambush_risk_percent: Math.round((security.ambush_probability || 0.01) * 100),
    ied_risk_percent: Math.round((security.ied_probability || 0.01) * 100),
    fpv_drone_risk_percent: Math.round(
      (security.fpv_drone_probability || 0.02) * 
      (weatherData.drone_threat_modifier || 1.0) * 
      (is_night ? 0.4 : 1.0) * 100
    ),
    artillery_risk_percent: Math.round((security.artillery_probability || 0.01) * 100),
    overall_risk_level: calculateOverallRisk(security, weatherData, is_night)
  };
  
  return {
    distance_km,
    effective_speed_kmh: Math.round(effectiveSpeed),
    travel_time_minutes: totalMinutes,
    travel_time_hours: Math.round(travelTime_hours * 10) / 10,
    delay_minutes: Math.round(delayMinutes),
    
    factors: {
      road_status,
      loc_security,
      bridge_status,
      weather,
      is_night,
      is_convoy,
      is_armored,
      patient_category,
      escort_required: security.escort_required,
      route_clearance: security.route_clearance_required
    },
    
    survival_impact: survivalImpact,
    risk_assessment: riskAssessment,
    
    warnings: generateRouteWarnings({
      road_status, loc_security, bridge_status, weather, is_night
    }),
    
    recommendations: generateRouteRecommendations({
      road_status, loc_security, weather, totalMinutes, patient_category, is_night
    })
  };
};

/**
 * Calculate overall risk level
 */
const calculateOverallRisk = (security, weather, isNight) => {
  const baseRisk = (
    (security.ambush_probability || 0) +
    (security.ied_probability || 0) +
    (security.fpv_drone_probability || 0) * (weather.drone_threat_modifier || 1) +
    (security.artillery_probability || 0)
  ) / 4;
  
  const nightModifier = isNight ? 0.6 : 1.0;
  const adjustedRisk = baseRisk * nightModifier;
  
  if (adjustedRisk >= 0.30) return 'CRITICAL';
  if (adjustedRisk >= 0.15) return 'HIGH';
  if (adjustedRisk >= 0.05) return 'MODERATE';
  return 'LOW';
};

/**
 * Generate route warnings based on configuration
 */
const generateRouteWarnings = (config) => {
  const warnings = [];
  
  const road = ROAD_STATUS[config.road_status];
  const security = LOC_SECURITY[config.loc_security];
  const bridge = BRIDGE_STATUS[config.bridge_status];
  const weather = WEATHER_ROUTE_IMPACTS[config.weather];
  
  if (road?.value === 'DENIED') {
    warnings.push({
      severity: 'error',
      code: 'ROUTE_DENIED',
      message: 'Route is impassable. Select alternate route.',
      recommendation: 'Consider air evacuation or alternate ground route.'
    });
  }
  
  if (road?.value === 'DAMAGED') {
    warnings.push({
      severity: 'warning',
      code: 'ROUTE_DAMAGED',
      message: 'Damaged road surface will significantly delay evacuation.',
      recommendation: 'Plan for extended travel times. Consider patient condition.'
    });
  }
  
  if (!road?.t1_transport_safe) {
    warnings.push({
      severity: 'warning',
      code: 'T1_UNSAFE',
      message: 'Route not safe for T1 patient transport - vibration risk.',
      recommendation: 'Use air evacuation for critical patients if available.'
    });
  }
  
  if (security?.value === 'INTERDICTED') {
    warnings.push({
      severity: 'error',
      code: 'ROUTE_INTERDICTED',
      message: 'High-risk route. Heavy escort required.',
      recommendation: 'Consider alternate route. Armored vehicles mandatory.'
    });
  }
  
  if (security?.value === 'CONTESTED') {
    warnings.push({
      severity: 'warning',
      code: 'ROUTE_CONTESTED',
      message: 'Route under enemy activity. Escort required.',
      recommendation: 'Coordinate with Route Security. Use armored ambulances.'
    });
  }
  
  if (!security?.medical_markings_safe) {
    warnings.push({
      severity: 'warning',
      code: 'MARKINGS_UNSAFE',
      message: 'Medical markings may attract targeting - Ukraine pattern.',
      recommendation: 'Consider camouflaging medical vehicles.'
    });
  }
  
  if (bridge?.value === 'DESTROYED') {
    warnings.push({
      severity: 'error',
      code: 'BRIDGE_DESTROYED',
      message: 'Bridge destroyed. Engineering support required.',
      recommendation: 'Plan alternate route or await bridging assets.'
    });
  }
  
  if (weather?.speed_modifier < 0.5) {
    warnings.push({
      severity: 'warning',
      code: 'SEVERE_WEATHER',
      message: 'Severe weather conditions affecting travel.',
      recommendation: 'Delay non-urgent evacuations. Increase time estimates.'
    });
  }
  
  if (config.weather === 'RASPUTITSA') {
    warnings.push({
      severity: 'error',
      code: 'RASPUTITSA',
      message: 'Mud season - unpaved routes effectively impassable.',
      recommendation: 'Use paved routes only or request air evacuation.'
    });
  }
  
  return warnings;
};

/**
 * Generate route recommendations
 */
const generateRouteRecommendations = (config) => {
  const recommendations = [];
  
  // Night movement recommendation for contested areas
  if ((config.loc_security === 'CONTESTED' || config.loc_security === 'INTERDICTED') && !config.is_night) {
    recommendations.push({
      priority: 'HIGH',
      type: 'TIMING',
      message: 'Consider night movement for contested routes',
      benefit: '65% reduction in engagement probability',
      tradeoff: '50% slower travel speed'
    });
  }
  
  // Air evacuation recommendation for T1 with long travel times
  if (config.patient_category === 'T1' && config.totalMinutes > 60) {
    recommendations.push({
      priority: 'HIGH',
      type: 'MEDEVAC',
      message: 'Ground evacuation exceeds Golden Hour',
      benefit: 'Air evacuation can reduce time by 60-70%',
      action: 'Request MEDEVAC if available'
    });
  }
  
  // Weather exploitation
  const weather = WEATHER_ROUTE_IMPACTS[config.weather];
  if (weather?.drone_threat_modifier < 0.5) {
    recommendations.push({
      priority: 'MEDIUM',
      type: 'WINDOW',
      message: 'Current weather reduces drone threat',
      benefit: `${Math.round((1 - weather.drone_threat_modifier) * 100)}% reduction in drone risk`,
      action: 'Exploit weather window for movement'
    });
  }
  
  return recommendations;
};

/**
 * Calculate safe evacuation window based on threats
 * Returns optimal movement times considering threats
 */
export const calculateEvacuationWindow = (config = {}) => {
  const {
    loc_security = 'SECURE',
    weather = 'CLEAR',
    artillery_active = false,
    drone_activity = 'NORMAL'
  } = config;
  
  const security = LOC_SECURITY[loc_security] || LOC_SECURITY.SECURE;
  const weatherData = WEATHER_ROUTE_IMPACTS[weather] || WEATHER_ROUTE_IMPACTS.CLEAR;
  const droneData = DRONE_ACTIVITY[drone_activity] || DRONE_ACTIVITY.NORMAL;
  
  // Calculate risk by time period
  const periods = {
    dawn: { start: '05:00', end: '07:00', base_risk: 0.7 },
    day: { start: '07:00', end: '18:00', base_risk: 1.0 },
    dusk: { start: '18:00', end: '20:00', base_risk: 0.7 },
    night: { start: '20:00', end: '05:00', base_risk: 0.4 }
  };
  
  const windows = {};
  
  Object.entries(periods).forEach(([period, data]) => {
    let risk = data.base_risk;
    
    // Apply drone threat modifier from DRONE_ACTIVITY constant
    if (period === 'night') {
      // Night effectiveness from drone activity level
      risk *= droneData.night_effectiveness || 0.4;
    } else if (period === 'day') {
      // Day risk multiplier from drone activity level
      risk *= droneData.day_risk_multiplier || 1.0;
    } else {
      // Dawn/dusk: average of day and night risk
      const dayRisk = droneData.day_risk_multiplier || 1.0;
      const nightRisk = droneData.night_effectiveness || 0.4;
      risk *= (dayRisk + nightRisk) / 2;
    }
    
    // Apply overall drone risk modifier
    risk *= droneData.risk_modifier || 1.0;
    
    // Apply weather's effect on drone operations
    risk *= weatherData.drone_threat_modifier || 1.0;
    
    // Apply artillery modifier
    if (artillery_active && period === 'day') {
      risk *= 1.5;  // Daytime artillery more accurate
    }
    
    // Apply security level
    risk *= (security.ambush_probability || 0.01) * 10;
    
    windows[period] = {
      ...data,
      adjusted_risk: Math.min(1.0, risk),
      risk_level: risk < 0.3 ? 'LOW' : risk < 0.6 ? 'MODERATE' : 'HIGH',
      recommended: risk < 0.4,
      drone_risk_factor: droneData.risk_modifier || 1.0
    };
  });
  
  // Find best window
  const bestWindow = Object.entries(windows)
    .sort((a, b) => a[1].adjusted_risk - b[1].adjusted_risk)[0];
  
  return {
    windows,
    recommended_window: bestWindow[0],
    recommended_times: bestWindow[1],
    drone_activity_level: droneData.label,
    drone_recommendations: droneData.recommendations || [],
    notes: [
      'Night movement recommended for contested areas',
      'Dawn/dusk provide balance of visibility and reduced threat',
      'Coordinate with counter-battery for artillery windows',
      ...(droneData.recommendations || [])
    ]
  };
};

/**
 * Get combined LOC modifiers for calculator
 */
export const getLOCModifiers = (config = {}) => {
  const {
    msr_status = 'PAVED',
    asr_status = 'PAVED',
    loc_security = 'SECURE',
    primary_bridge = 'INTACT',
    weather = 'CLEAR'
  } = config;
  
  const msr = ROAD_STATUS[msr_status] || ROAD_STATUS.PAVED;
  const asr = ROAD_STATUS[asr_status] || ROAD_STATUS.PAVED;
  const security = LOC_SECURITY[loc_security] || LOC_SECURITY.SECURE;
  const bridge = BRIDGE_STATUS[primary_bridge] || BRIDGE_STATUS.INTACT;
  const weatherData = WEATHER_ROUTE_IMPACTS[weather] || WEATHER_ROUTE_IMPACTS.CLEAR;
  
  // Calculate overall speed modifier
  const avgRoadModifier = (msr.speed_modifier + asr.speed_modifier) / 2;
  const overallSpeedModifier = avgRoadModifier * security.speed_modifier * weatherData.speed_modifier;
  
  // Calculate evacuation time modifier (inverse of speed)
  const evacTimeModifier = 1 / Math.max(overallSpeedModifier, 0.1);
  
  // Determine evacuation paradigm
  const paradigm = security.evacuation_paradigm || 'PERMISSIVE';
  
  return {
    speed_modifier: Math.round(overallSpeedModifier * 100) / 100,
    evac_time_modifier: Math.round(evacTimeModifier * 100) / 100,
    
    evacuation_paradigm: paradigm,
    
    escort_required: security.escort_required || false,
    armored_required: security.armored_vehicles_required || false,
    movement_window: security.movement_window,
    medical_markings_safe: security.medical_markings_safe !== false,
    
    bridge_usable: bridge.usable !== false,
    bridge_mlc: bridge.mlc_rating || 70,
    
    coordination_delay_minutes: Number.isFinite(security.delay_minutes) ? security.delay_minutes : 0,
    
    // Risk factors for calculator
    convoy_casualty_risk: Math.round(
      ((security.ambush_probability || 0) + 
       (security.ied_probability || 0) + 
       (security.fpv_drone_probability || 0) * (weatherData.drone_threat_modifier || 1)) / 3 * 100
    ) / 100,
    
    // T1 transport safe
    t1_ground_safe: msr.t1_transport_safe && asr.t1_transport_safe,
    
    warnings: generateRouteWarnings({
      road_status: msr_status, loc_security, bridge_status: primary_bridge, weather
    })
  };
};

/**
 * Calculate time-to-role by ground
 * Integrates with evacuation planning
 */
export const calculateGroundEvacTime = (
  poi_km,           // Point of Injury to Role 1
  role1_km,         // Role 1 to Role 2
  role2_km,         // Role 2 to Role 3
  config = {}
) => {
  const {
    road_status = 'PAVED',
    loc_security = 'SECURE',
    weather = 'CLEAR',
    is_night = false
  } = config;
  
  // Get configuration data for summary
  const roadData = ROAD_STATUS[road_status] || ROAD_STATUS.PAVED;
  const securityData = LOC_SECURITY[loc_security] || LOC_SECURITY.SECURE;
  const weatherData = WEATHER_ROUTE_IMPACTS[weather] || WEATHER_ROUTE_IMPACTS.CLEAR;
  
  // Calculate each segment
  const poiToRole1 = calculateTravelTime(poi_km, {
    ...config,
    vehicle_type: 'ambulance',
    patient_category: 'T1'
  });
  
  const role1ToRole2 = calculateTravelTime(role1_km, {
    ...config,
    vehicle_type: 'ambulance',
    patient_category: 'T1'
  });
  
  const role2ToRole3 = calculateTravelTime(role2_km, {
    ...config,
    vehicle_type: 'ambulance',
    patient_category: 'T2'  // Post-surgery patients more stable
  });
  
  // Add processing times at each role
  const role1_processing_min = 15;    // Stabilization
  const role2_processing_min = 90;    // DCS if needed
  const role2_to_role3_hold_hours = 4; // Standard hold before onward evac
  
  const timeToRole1 = poiToRole1.travel_time_minutes;
  const timeToRole2 = timeToRole1 + role1_processing_min + role1ToRole2.travel_time_minutes;
  const timeToRole3 = timeToRole2 + role2_processing_min + (role2_to_role3_hold_hours * 60) + role2ToRole3.travel_time_minutes;
  
  // Calculate overall conditions summary
  const conditionsSummary = {
    road_status: {
      value: road_status,
      label: roadData.label,
      speed_modifier: roadData.speed_modifier,
      t1_safe: roadData.t1_transport_safe
    },
    security: {
      value: loc_security,
      label: securityData.label,
      speed_modifier: securityData.speed_modifier,
      escort_required: securityData.escort_required
    },
    weather: {
      value: weather,
      label: weatherData.label,
      speed_modifier: weatherData.road_impact?.[road_status] || 1.0,
      drone_threat_modifier: weatherData.drone_threat_modifier
    },
    is_night,
    night_modifier: is_night ? (roadData.night_speed_modifier || 0.8) * (weatherData.night_modifier || 0.8) : 1.0
  };
  
  return {
    // Input summary using destructured variables
    conditions: conditionsSummary,
    
    segments: {
      poi_to_role1: poiToRole1,
      role1_to_role2: role1ToRole2,
      role2_to_role3: role2ToRole3
    },
    
    cumulative_minutes: {
      to_role1: timeToRole1,
      to_role2: timeToRole2,
      to_role3: timeToRole3
    },
    
    golden_hour_status: {
      to_role1: timeToRole1 <= 60 ? 'ACHIEVED' : 'EXCEEDED',
      to_role2: timeToRole2 <= 120 ? 'ACCEPTABLE' : 'DELAYED'
    },
    
    t1_survival_estimate: {
      at_role1: estimateSurvival(timeToRole1),
      at_role2: estimateSurvival(timeToRole2)
    },
    
    recommendations: generateEvacRecommendations(timeToRole1, timeToRole2, config)
  };
};

/**
 * Estimate T1 survival probability based on evacuation time
 */
const estimateSurvival = (minutes) => {
  if (minutes <= 10) return 0.95;
  if (minutes <= 30) return 0.92;
  if (minutes <= 60) return 0.85;
  if (minutes <= 90) return 0.70;
  if (minutes <= 120) return 0.54;
  if (minutes <= 180) return 0.32;
  return Math.max(0.18, 0.32 - (minutes - 180) / 180 * 0.14);
};

/**
 * Generate evacuation recommendations
 */
const generateEvacRecommendations = (toRole1, toRole2, config) => {
  const recommendations = [];
  
  if (toRole1 > 60) {
    recommendations.push({
      priority: 'CRITICAL',
      type: 'TIME',
      message: `Time to Role 1 (${toRole1} min) exceeds Golden Hour`,
      actions: [
        'Request MEDEVAC helicopter',
        'Initiate Prolonged Field Care',
        'Consider forward surgical team'
      ]
    });
  }
  
  if (toRole1 > 10 && toRole1 <= 60) {
    recommendations.push({
      priority: 'HIGH',
      type: 'TCCC',
      message: 'Platinum 10 minutes exceeded - TCCC interventions critical',
      actions: [
        'Ensure tourniquet applied <5 min from wounding',
        'TXA administration if hemorrhage',
        'Hypothermia prevention'
      ]
    });
  }
  
  if (config.loc_security === 'CONTESTED' || config.loc_security === 'INTERDICTED') {
    recommendations.push({
      priority: 'HIGH',
      type: 'SECURITY',
      message: 'Route security compromised',
      actions: [
        'Use armored ambulances',
        'Consider night movement',
        'Coordinate escort'
      ]
    });
  }
  
  return recommendations;
};

/**
 * Validate LOC configuration
 */
export const validateLOCConfiguration = (config = {}) => {
  const errors = [];
  const warnings = [];
  
  // Check for denied routes
  if (config.msr_status === 'DENIED' && config.asr_status === 'DENIED') {
    errors.push({
      code: 'NO_GROUND_ROUTE',
      message: 'Both MSR and ASR are denied. No ground evacuation possible.',
      recommendation: 'Request air evacuation assets or engineer route clearance.'
    });
  }
  
  // Check security vs road condition mismatch
  if (config.msr_status === 'PAVED' && config.loc_security === 'INTERDICTED') {
    warnings.push({
      code: 'ROAD_SECURITY_MISMATCH',
      message: 'Good road condition but interdicted - enemy may have prepared ambush sites.',
      recommendation: 'Expect enemy observation and targeting along route.'
    });
  }
  
  // Check bridge access
  if (config.primary_bridge === 'DESTROYED' && !config.alternate_crossing) {
    errors.push({
      code: 'NO_CROSSING',
      message: 'Primary bridge destroyed with no alternate crossing defined.',
      recommendation: 'Identify ford, ferry, or alternate bridge.'
    });
  }
  
  // Check for rasputitsa
  if (config.weather === 'RASPUTITSA' && (config.msr_status === 'UNPAVED' || config.asr_status === 'UNPAVED')) {
    errors.push({
      code: 'RASPUTITSA_UNPAVED',
      message: 'Mud season with unpaved routes - expect severe delays or impassability.',
      recommendation: 'Use paved routes only or request air evacuation.'
    });
  }
  
  return { 
    errors, 
    warnings, 
    isValid: errors.length === 0,
    severity: errors.length > 0 ? 'ERROR' : warnings.length > 0 ? 'WARNING' : 'OK'
  };
};

// ============================================================================
// DEFAULT CONFIGURATIONS
// ============================================================================

export const DEFAULT_LOC_CONFIG = {
  msr_status: 'PAVED',
  asr_status: 'IMPROVED',
  loc_security: 'SECURE',
  primary_bridge: 'INTACT',
  weather: 'CLEAR'
};

export const HIGH_THREAT_LOC_CONFIG = {
  msr_status: 'DAMAGED',
  asr_status: 'UNPAVED',
  loc_security: 'CONTESTED',
  primary_bridge: 'TEMPORARY',
  weather: 'RAIN'
};

export const UKRAINE_PATTERN_CONFIG = {
  msr_status: 'DAMAGED',
  asr_status: 'DAMAGED',
  loc_security: 'INTERDICTED',
  primary_bridge: 'DESTROYED',
  weather: 'CLEAR',
  drone_threat: 'HIGH',
  artillery_active: true,
  medical_marking_safe: false
};

export const ROMANIA_EXERCISE_CONFIG = {
  msr_status: 'PAVED',
  asr_status: 'IMPROVED',
  loc_security: 'GUARDED',
  primary_bridge: 'INTACT',
  weather: 'CLEAR',
  mountain_passes: true
};

// ============================================================================
// EDUCATIONAL CONTENT
// ============================================================================

export const LOC_EDUCATIONAL = {
  key_principles: [
    'LOC security directly impacts evacuation time and patient survival',
    'Weather affects both road conditions and threat levels (drones)',
    'Night movement trades speed for safety in contested areas',
    'Bridge availability is critical - always have alternates',
    'Ukraine 2022-2024 shows medical vehicles are specifically targeted'
  ],
  
  common_mistakes: [
    'Assuming medical markings provide protection (they attract targeting)',
    'Underestimating rasputitsa (mud season) impact',
    'Not pre-planning alternate routes',
    'Ignoring weather windows for movement',
    'Traveling in predictable patterns'
  ],
  
  best_practices: [
    'Coordinate with counter-battery for artillery windows',
    'Use weather conditions that ground drones for movement',
    'Maintain tactical dispersion in convoys',
    'Pre-position guides for night movement',
    'Vary routes, times, and tactics'
  ]
};

// ============================================================================
// EXPORT ALL
// ============================================================================

// Named default export (fixes lint warning)
const locConfigConstants = {
  ROAD_STATUS,
  LOC_SECURITY,
  DRONE_ACTIVITY,
  BRIDGE_STATUS,
  ROUTE_TYPES,
  WEATHER_ROUTE_IMPACTS,
  UKRAINE_ROUTE_LESSONS,
  ROMANIA_ROUTE_FACTORS,
  calculateTravelTime,
  calculateEvacuationWindow,
  getLOCModifiers,
  calculateGroundEvacTime,
  validateLOCConfiguration,
  DEFAULT_LOC_CONFIG,
  HIGH_THREAT_LOC_CONFIG,
  UKRAINE_PATTERN_CONFIG,
  ROMANIA_EXERCISE_CONFIG,
  LOC_EDUCATIONAL
};

export default locConfigConstants;
